/**
 * Issue #3724: whether the active incidence of the device blocks MOS operations (starting or
 * switching a fabrication order). The flag is the "Blocks production" column of the incidence pick list
 * (Sun exposes it as `incidence.blocksProduction` on the active device incidence), so the front no
 * longer hardcodes which device statuses block. A "tooling change" incidence can leave the machine
 * STOPPED and still let the operator pick the next fabrication order; a "breakdown" one blocks.
 * @param {object} [activeIncidence] Active device incidence as returned by `mos/incidences`.
 * @returns {boolean}
 */
export const isBlockedByIncidence = (activeIncidence) => {
  return activeIncidence?.incidence?.blocksProduction === true
}

// i18n key of the tooltip and of the snackbar of a Start/Join withheld by Single Job Mode.
export const SINGLE_JOB_MODE_BLOCK_KEY = 'Single job mode: interrupt or finish the active job before starting another one'

// A withheld Start/Join keeps its label and its place (a row must never suddenly read "Finish"), LOOKS
// disabled, and stays clickable so the click can explain why instead of starting anything.
export const SINGLE_JOB_MODE_BLOCKED_BUTTON_SX = { opacity: 0.5 }

const sameId = (left, right) => left !== undefined && left !== null && right !== undefined && right !== null && String(left) === String(right)

const isSameJob = (activeJob, fabricationOrderId, jobReferenceId) =>
  sameId(activeJob?.fabricationOrder?.id, fabricationOrderId) && sameId(activeJob?.jobReference?.id, jobReferenceId)

/**
 * Single Job Mode (a setting of the Operation widget, published by the screen as
 * `globalData.singleJobMode`, see `singleJobMode.js`): while it is on and the machine already runs a
 * job, no OTHER job may be started or joined from any MOS surface. The job that is itself active is
 * never blocked by its own presence, so the rule stays correct whichever row it is asked about.
 * @param {object} params
 * @param {object} [params.globalData] MOS `globalData`
 * @param {Array} [params.activeJobs] `activeJobs` of the MOS context (`mos/device/{id}/active-context`)
 * @param {string|number} [params.fabricationOrderId] fabrication order of the job being started
 * @param {string|number} [params.jobReferenceId] job reference of the job being started
 * @returns {boolean}
 */
export const isBlockedBySingleJobMode = ({ globalData, activeJobs = [], fabricationOrderId, jobReferenceId } = {}) => {
  if (globalData?.singleJobMode !== true) return false
  const jobs = Array.isArray(activeJobs) ? activeJobs : []
  return jobs.some((activeJob) => !isSameJob(activeJob, fabricationOrderId, jobReferenceId))
}

export const canStartJob = ({ globalData, statusType, activeJobs, fabricationOrderId, jobReferenceId }) => {
  const blocked = isBlockedByIncidence(globalData?.incidence)
  const notAvailable = statusType === 'NOT AVAILABLE'
  const singleJobBlocked = isBlockedBySingleJobMode({ globalData, activeJobs, fabricationOrderId, jobReferenceId })
  return !blocked && !notAvailable && !singleJobBlocked
}

export const getStartBlockReason = ({ globalData, t_, activeJobs, fabricationOrderId, jobReferenceId }) => {
  if (isBlockedByIncidence(globalData?.incidence)) {
    return t_ ? t_('Device has an active incidence') : 'Device has an active incidence'
  }
  if (isBlockedBySingleJobMode({ globalData, activeJobs, fabricationOrderId, jobReferenceId })) {
    return t_ ? t_(SINGLE_JOB_MODE_BLOCK_KEY) : SINGLE_JOB_MODE_BLOCK_KEY
  }
  return null
}
